/**
 * IMPORTS
 */
import customers from 'src/aggregates/customers/selectors';
import {contactTypesIdToChannel} from 'src/aggregates/customers/utils';
import Store from 'src/store';


/**
 * TYPES
 */
import {IPush} from 'src/aggregates/conversations/session/session.d';
import {IAgentsMetrics} from 'src/aggregates/conversations/state.d';
import {IChatState} from 'src/aggregates/conversations/state.d';
import {IConversation} from 'src/aggregates/conversations/state.d';
import {IMetrics} from 'src/aggregates/conversations/state.d';
import {IMetricsPeriod} from 'src/aggregates/conversations/state.d';
import {IContact} from 'src/aggregates/customers/state.d';
import {IMessage} from 'src/aggregates/timelines/state.d';


/**
 * CODE
 */

/**
 * I retrieve a conversation if it exists.
 *
 * :param id: conversation id
 *
 * :returns: conversation or undefined
 */
const get = (id: number): IConversation | null =>
{
    // look up conversation by id
    const conversation = getState().byId[id];

    // return conversation or null if it does not exist
    return conversation ? conversation : null;
};


/**
 * I get a conversation with the given contact.
 *
 * :param channel: contact channel
 * :param uid: contact uid
 *
 * :returns: conversation or null on no ongoing conversation with the contact
 */
const getConversationByContact = (
    channel: string,
    uid: string,
): IConversation | null =>
{
    // get all conversations as an array
    const conversations: IConversation[] = Object.values(getState().byId);

    // search for the conversation that matches given contact
    for (const conversation of conversations)
    {
        // conversation contact matches given contact: return conversation
        if (conversation.channel === channel && conversation.remote === uid)
        {
            return conversation;
        }
    }

    // none conversation matched given contact: return null
    return null;
};


/**
 * I get a conversation with the given account and contact.
 *
 * :param account: account id
 * :param uid: contact uid
 *
 * :returns: conversation or null on no ongoing conversation with the contact
 */
const getConversationByAccountAndUid = (
    account: number,
    uid: string,
): IConversation | null =>
{
    // get all conversations as an array
    const conversations: IConversation[] = Object.values(getState().byId);

    // search for the conversation that matches given contact
    for (const conversation of conversations)
    {
        // conversation contact matches given contact: return conversation
        if (conversation.account === account && conversation.remote === uid)
        {
            return conversation;
        }
    }

    // none conversation matched given contact and account: return null
    return null;
};


/**
 * I get all conversations from the given customer.
 *
 * :param customer: customer id
 *
 * :returns: customer conversations
 */
function getConversationsByCustomer (
    customer: string | number,
): IConversation[]
{
    // get customer contacts values
    const {contacts} = customers.fetch(customer);

    // get contacts conversations
    const conversations = contacts.map(
        contact => getConversationByContact(
            contactTypesIdToChannel.get(contact.type_id).toLowerCase(),
            contact.value,
        ),
    );

    // return founded conversations
    return conversations.filter(conversation => conversation !== null);
}


/**
 * I get the customer latest interaction.
 *
 * :param contacts: all customer contacts
 *
 * :returns: customer latest interaction
 */
const getCustomerLatestInteraction = (contacts: IContact[]): number =>
{
    // initialize latest interaction
    let latest = 0;

    // get all conversations as an array
    const conversations: IConversation[] = Object.values(getState().byId);

    // get the latest contact last interaction
    for (const contact of contacts)
    {
        // get conversations with the given contact
        const filtered = conversations.filter(
            (conversation: IConversation): boolean =>
            {

                // get contact channel as a string
                const channel = contactTypesIdToChannel.get(contact.type_id)
                    .toLowerCase();

                // return if the conversation contact matches given contact
                return conversation.remote === contact.value &&
                       conversation.channel === channel;
            },
        );

        // get the latest interaction among all conversations
        latest = filtered.reduce(
            (biggest: number, current: IConversation): number =>
            {

                // get current conversation last interaction
                const last = current.lastInteraction;

                // return biggest value between the stored biggest and last
                return biggest > last ? biggest : last;
            },
            latest,
        );
    }

    // return latest interaction
    return latest;
};


/**
 * I get the amount of unsnawered messages of all customer contacts.
 *
 * :param contacts: all customer contacts
 *
 * :returns: customer ammount of unsanwered messages
 */
const getCustomerUnansweredMessages = (contacts: IContact[]): number =>
{
    // initialize unanswered messages counter
    let unanswered = 0;

    // get all conversations as an array
    const conversations: IConversation[] = Object.values(getState().byId);

    // add each contact ongoing conversation ammount of unsanwered messages
    for (const contact of contacts)
    {
        // get conversations with the given contact
        const filtered = conversations.filter(
            (conversation: IConversation): boolean =>
            {

                // get contact channel as a string
                const channel = contactTypesIdToChannel.get(contact.type_id)
                    .toLowerCase();

                // return if the conversation contact matches given contact
                return conversation.remote === contact.value &&
                       conversation.channel === channel;
            },
        );

        // sum all unanswered values of each conversation and add to counter
        unanswered += filtered.reduce(
            (accumulator: number, current: IConversation): number =>
            {
                return accumulator + current.unansweredMessages;
            },
            0,
        );
    }

    // return unanswered messages counter
    return unanswered;
};


/**
 * I get the customers with at least one contact.
 *
 * :param customers: customers list
 *
 * :returns: customers with at least one contact
 */
const getCustomersWithContacts = (customers): object =>
{
    // initialize new customers
    const newCustomers = {};

    // get all customers that has contacts
    const filteredCustomers: any[] = Object.values(customers).filter(
        ({contacts}): boolean => contacts.length > 0,
    );

    // set new customers
    for (const customer of filteredCustomers)
    {
        newCustomers[customer.id] = {...customer};
    }

    // return filtered customers
    return newCustomers;
};


/**
 * I retrieve a finished conversation if it exists.
 *
 * :param id: conversation id
 *
 * :returns: conversation or null
 */
const getFinished = (id: number): IConversation | null =>
{
    // look up conversation by id
    const conversation = getState().finishedById[id];

    // return conversation or null if it does not exist
    return conversation ?? null;
};


/**
 * I get last message of customer.
 *
 * :param contacts: customer contacts
 *
 * :returns: last message or null
 */
function getLastMessage(contacts: IContact[]): IMessage | null
{
    // get all conversations
    const conversations: IConversation[] = Object.values(getState().byId);

    // initialize filtered conversations
    const filtered: IConversation[] = [];

    // for each contact: filter conversations
    for (const contact of contacts)
    {
        // for each conversation: filter by contact
        for (const conversation of conversations)
        {
            // get contact channel
            const channel = contactTypesIdToChannel.get(contact.type_id)
                .toLowerCase();

            // conversation is of contact: push to filtered list
            if (conversation.remote === contact.value &&
                conversation.channel === channel)
            {
                filtered.push(conversation);
            }
        }
    }

    // no conversations of customer contacts: return null
    if (filtered.length === 0)
    {
        return null;
    }

    // get last conversation
    let lastConversation = filtered[0];
    if (filtered.length > 1)
    {
        lastConversation = filtered.sort((a, b) => (
            a.lastInteraction > b.lastInteraction ? -1 : 1
        ))[0];
    }

    // get last message
    const messages: IMessage[] = Object.values(lastConversation.messages ?? {});
    let lastMessage = messages[0] ?? null;

    // no conversation messages: return null
    if (lastMessage === null)
    {
        return null;
    }

    // has several messages: get the last
    if (messages.length > 1)
    {
        lastMessage = messages.sort((a, b) => (
            a.timestamp > b.timestamp ? -1 : 1
        ))[0];
    }

    // return last message
    return lastMessage;
}


/*
 * I get last messages metrics period.
 *
 * :returns: timestamps
 */
function getLastMessagesMetricsPeriod(): IMetricsPeriod
{
    // get period
    const {
        lastEnd,
        lastStart,
    } = Store.getState().conversation.metrics.agents as IAgentsMetrics;

    // return period
    return {end: lastEnd, start: lastStart};
}


/*
 * I get last metrics period.
 *
 * :returns: timestamps
 */
function getLastMetricsPeriod(): IMetricsPeriod
{
    // get period
    const {
        lastEnd,
        lastStart,
    } = Store.getState().conversation.metrics as IMetrics;

    // return period
    return {end: lastEnd, start: lastStart};
}


/*
 * I get metrics.
 *
 * :returns: metrics
 */
function getMetrics(): IMetrics
{
    return Store.getState().conversation.metrics as IMetrics;
}


/**
 * I convert a messages object to an array sorted by id.
 *
 * :param messages: messages
 *
 * :returns: array of messages sorted by id
 */
const getMessagesAsSortedArray = (messages: object): any[] =>
{
    // get messages as an array
    const array = Object.values(messages);

    // return messages sorted by id
    return array.sort((current: any, next: any): number =>
    {
        // no id for current: put it after
        if (current.id === undefined || current.id === null)
        {
            return 1;
        }

        // no id for next: put it before
        if (next.id === undefined || next.id === null)
        {
            return -1;
        }

        // use id to sort
        return current.id - next.id;
    });
};


/**
 * I return whether specified contacts have an auto-answered conversation.
 *
 * :param contacts: all customer contacts
 *
 * :returns: whether some contact has an auto answered conversation
 */
const isAutoAnswered = (contacts: IContact[]): boolean =>
{
    // initialize with no auto-answered
    let isAutoAnswer = false;

    // get all conversations as an array
    const conversations: IConversation[] = Object.values(getState().byId);

    // check all contacts for auto-answered conversations
    for (const contact of contacts)
    {
        // get conversations with the given contact
        const filtered = conversations.filter(
            (conversation: IConversation): boolean =>
            {
                // get contact channel as a string
                const channel = contactTypesIdToChannel.get(contact.type_id)
                    .toLowerCase();

                // return if the conversation contact matches given contact
                return conversation.remote === contact.value &&
                       conversation.channel === channel;
            },
        );

        // update result for current contact
        isAutoAnswer = isAutoAnswer || filtered.some(
            (current: IConversation) => current.isAutoAnswered === true,
        );

        // if some contact has auto answered: return true
        if (isAutoAnswer === true)
        {
            return isAutoAnswer;
        }
    }

    // return isAutoAnswer value
    return isAutoAnswer;
};


/**
 * I get metrics start and end timestamps.
 *
 * :returns: metrics start and end timestamps array
 */
function getMetricsPeriod(): IMetricsPeriod
{
    // get metrics start and end timestamps
    const {startDate, endDate} = Store.getState().conversation.metrics;

    // return metrics start and end timestamps
    return {end: endDate, start: startDate};
}


/**
 * I get the push data.
 *
 * :returns: push data
 */
function getPushData(): IPush | null
{
    return getState()?.user?.push ?? null;
}


/**
 * I get conversations session token.
 *
 * :returns: conversations session token
 */
function getSession(): string
{
    return getState().user.session;
}


/**
 * I get conversations aggregate state.
 *
 * :returns: conversations aggregate state
 */
function getState(): IChatState
{
    return Store.getState().conversation;
}


/**
 * I get the amount of ongoing conversations with unanswered messages.
 *
 * :returns: amount of ongoing conversations with unanswered messages
 */
function getUnansweredConversations(): number
{
    // initialize unanswered counter
    let unanswered = 0;

    // get ongoing conversations
    const conversations: IConversation[] = Object.values(getState().byId);

    // count conversations with unanswered messages
    for (const conversation of conversations)
    {
        if (conversation.unansweredMessages > 0)
        {
            unanswered = unanswered + 1;
        }
    }

    // amount of ongoing conversations with unanswered messages
    return unanswered;
}


/**
 * I list all conversations with the given contact.
 *
 * :param uid: contact uid
 * :param account: conversation account
 *
 * :returns: list of conversations
 */
function listConversationsByContact (
    account: number,
    uid: string,
): IConversation[]
{
    // get all conversations as an array
    const conversations: IConversation[] = Object.values(getState().byId);

    // initiliaze result
    const result: IConversation[] = [];

    // search for all conversations with given contact
    for (const conversation of conversations)
    {
        // conversation with given contact: add it to result
        if (conversation.remote === uid && conversation.account === account)
        {
            result.push(conversation);
        }
    }

    // return result
    return result;
}


/**
 * I sort a conversation list by last interaction.
 *
 * :param conversations: conversation list
 *
 * :returns: array of conversations sorted by timestamp
 */
const sortConversationsByLastMessage = (conversations: object): any[] =>
{
    // get conversations as an array
    const array = Object.values(conversations);

    // return conversations sorted by last interaction
    return array.sort((a: any, b: any): number => (
        a.lastInteraction - b.lastInteraction
    ));
};


/**
 * I get a message from a given conversation.
 *
 * :param conversationId: conversation id
 * :param messageId: message id
 *
 * :returns: message or null
 */
function getMessageById(conversationId: number, messageId: number)
{
    // get conversation
    const conversation = get(conversationId);

    // conversation doesn't exists: return null
    if (conversation === null)
    {
        return null;
    }

    // conversation exists: get message
    const message = conversation.messages[messageId];

    // message doesn't exists: return null
    if (message === undefined)
    {
        return null;
    }

    // message exists: return it
    return message;
}


/**
 * EXPORTS
 */
export default {
    get,
    getConversationByAccountAndUid,
    getConversationByContact,
    getConversationsByCustomer,
    getCustomerLatestInteraction,
    getCustomerUnansweredMessages,
    getCustomersWithContacts,
    getFinished,
    getLastMessage,
    getLastMessagesMetricsPeriod,
    getLastMetricsPeriod,
    getMessageById,
    getMessagesAsSortedArray,
    getMetrics,
    getMetricsPeriod,
    getPushData,
    getSession,
    getState,
    getUnansweredConversations,
    isAutoAnswered,
    listConversationsByContact,
    sortConversationsByLastMessage,
};
